import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader, LogOut } from 'lucide-react';
import config from '../../config';
import EtapeTypeCompte from './EtapeTypeCompte';
import EtapeClient from './EtapeClient';
import EtapeSite from './EtapeSite';
import EtapeConnecteur from './EtapeConnecteur';
import EtapeCompte from './EtapeCompte';

const API_URL = config.API_URL;

/**
 * Le parcours d'installation d'un compte neuf, en cinq étapes.
 *
 * ## L'ordre suit le modèle, pas la technique
 *
 * Client → site → connecteur → compte → dashboard. C'est l'inverse de l'assistant du
 * Gestionnaire (`kanban/wizard/SetupWizard.jsx`), qui part du connecteur parce qu'il s'adresse à
 * quelqu'un d'installé. Ici la personne découvre l'outil : lui faire nommer son client puis son
 * site lui apprend la hiérarchie dont dépendent le dashboard consolidé, le filtre client et les
 * accès partagés — hiérarchie qu'elle devrait sinon deviner devant une application vide.
 *
 * ## L'étape vient du serveur, jamais de la mémoire
 *
 * L'étape du connecteur **fait sortir du site** : on part chez Google ou Meta, on revient sur
 * une page rechargée, et tout état local est perdu. L'étape courante est donc relue à chaque
 * montage par `GET /api/onboarding/etat`, qui la déduit de ce qui existe réellement. Aucun
 * paramètre d'URL n'a à transporter la progression, ce qui rend le parcours insensible à un
 * refus de consentement, à une fermeture du navigateur pendant la redirection, ou à un retour
 * dans un autre onglet.
 *
 * Le corollaire : après chaque action, on **recharge l'état** plutôt que d'avancer un compteur.
 * `rafraichir()` est la seule façon de changer d'étape.
 *
 * ## Pas de sortie, et ce que cela impose
 *
 * Le parcours est obligatoire : aucun lien « passer », aucune navigation. La seule porte est la
 * déconnexion, dans le menu de l'en-tête — ce qui rend d'autant plus important que chaque étape
 * sache dire pourquoi elle échoue, faute de quoi la personne serait enfermée devant un refus
 * muet.
 */
function ParcoursInitial({ user, token, onLogout, onTermine }) {
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [menuOuvert, setMenuOuvert] = useState(false);
  const profilRef = useRef(null);

  const rafraichir = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/onboarding/etat`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEtat(data);
      setErreur(null);
      return data;
    } catch (e) {
      setErreur(e.response?.data?.error || "Impossible de lire l'état du parcours");
      return null;
    }
  }, [token]);

  useEffect(() => { rafraichir(); }, [rafraichir]);

  // Deux onglets ouverts, ou un retour d'onglet après une connexion faite ailleurs : l'état
  // local serait périmé. On le relit quand la fenêtre reprend la main.
  useEffect(() => {
    const auRetour = () => { if (document.visibilityState === 'visible') rafraichir(); };
    window.addEventListener('focus', auRetour);
    document.addEventListener('visibilitychange', auRetour);
    return () => {
      window.removeEventListener('focus', auRetour);
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, [rafraichir]);

  useEffect(() => {
    if (!menuOuvert) return undefined;
    const auClic = (e) => {
      if (profilRef.current && !profilRef.current.contains(e.target)) setMenuOuvert(false);
    };
    document.addEventListener('click', auClic);
    return () => document.removeEventListener('click', auClic);
  }, [menuOuvert]);

  const nomAffiche = user?.name || (user?.email || '').split('@')[0];

  const etapes = [
    { cle: 'nature', libelle: 'Vous' },
    { cle: 'client', libelle: 'Client' },
    { cle: 'site', libelle: 'Site' },
    { cle: 'connecteur', libelle: 'Connecteur' },
    { cle: 'compte', libelle: 'Compte' }
  ];
  const rang = etapes.findIndex((e) => e.cle === etat?.etape);

  const contenu = () => {
    if (erreur) {
      return (
        <div style={{ textAlign: 'center', maxWidth: '420px' }}>
          <p style={{ color: '#b91c1c', fontSize: '15px', marginBottom: '16px' }}>{erreur}</p>
          <button onClick={rafraichir} style={boutonSecondaire}>Réessayer</button>
        </div>
      );
    }
    if (!etat) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#6b7280' }}>
          <Loader className="spin" size={28} style={{ marginBottom: '12px' }} />
          <span style={{ fontSize: '14px' }}>Chargement…</span>
        </div>
      );
    }

    const commun = { token, etat, onAvance: rafraichir };
    switch (etat.etape) {
      case 'nature': return <EtapeTypeCompte {...commun} />;
      case 'client': return <EtapeClient {...commun} />;
      case 'site': return <EtapeSite {...commun} />;
      case 'connecteur': return <EtapeConnecteur {...commun} />;
      case 'compte': return <EtapeCompte {...commun} onTermine={onTermine} />;
      default:
        // L'état dit « terminé » alors que le parcours est encore affiché : la clôture n'a pas
        // abouti (réseau coupé au dernier appel). On la propose plutôt que de laisser un écran
        // sans issue.
        return (
          <div style={{ textAlign: 'center', maxWidth: '420px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1f2937', margin: '0 0 12px' }}>
              Tout est prêt
            </h3>
            <p style={{ color: '#6b7280', fontSize: '15px', margin: '0 0 24px' }}>
              Votre premier compte est connecté et rattaché à votre site.
            </p>
            <button onClick={() => onTermine?.()} style={boutonPrincipal}>Ouvrir mon tableau de bord</button>
          </div>
        );
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#FAF9F5',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box'
    }}>
      <div className="app-header" style={{ padding: '12px 24px', marginBottom: 0 }}>
        <div className="header-left-group">
          <div className="logo-mini" style={{ cursor: 'default' }}>
            <img src="/kokpit-logo.png" alt="Kokpit" style={{ height: '38px' }} />
          </div>
        </div>

        {user && (
          <div className="user-profile" ref={profilRef} style={{ position: 'relative' }}>
            {user.picture ? (
              <img src={user.picture} alt={nomAffiche} className="user-avatar" />
            ) : (
              <div className="user-avatar-placeholder" aria-hidden="true">
                {(user.name || user.email || '?')[0].toUpperCase()}
              </div>
            )}
            {/* `.user-dropdown-toggle` est en `color: white` : sans cette couleur explicite, le
                chevron disparaîtrait sur le fond crème de cette page. */}
            <button
              className="user-dropdown-toggle"
              style={{ color: '#1a1a1a' }}
              onClick={(e) => { e.stopPropagation(); setMenuOuvert(!menuOuvert); }}
            >
              <span className="user-name">{nomAffiche}</span>
              <span className="dropdown-arrow">⌄</span>
            </button>
            {menuOuvert && (
              <div className="user-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => { setMenuOuvert(false); onLogout?.(); }}
                  className="dropdown-item logout-item"
                >
                  <LogOut size={16} />
                  Déconnexion
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Le fil d'étapes : il situe, il ne navigue pas. Revenir en arrière n'aurait rien à
          défaire — chaque étape est acquise dès qu'elle a créé son objet. */}
      {etat && rang >= 0 && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '8px',
          padding: '8px 24px 0', flexWrap: 'wrap'
        }}>
          {etapes.map((e, i) => (
            <div key={e.cle} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                fontSize: '12px',
                fontWeight: i === rang ? 600 : 400,
                color: i < rang ? '#1a1a1a' : i === rang ? '#1a1a1a' : '#9ca3af'
              }}>
                {i < rang ? '✓ ' : ''}{e.libelle}
              </span>
              {i < etapes.length - 1 && (
                <span style={{ color: '#d1d5db', fontSize: '12px' }}>—</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px 64px'
      }}>
        {contenu()}
      </div>
    </div>
  );
}

const boutonPrincipal = {
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  padding: '12px 24px', backgroundColor: '#1a1a1a', color: 'white',
  border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'pointer'
};

const boutonSecondaire = {
  padding: '10px 20px', background: 'white', border: '1px solid #d1d5db',
  borderRadius: '8px', fontSize: '14px', cursor: 'pointer'
};

export { boutonPrincipal, boutonSecondaire };
export default ParcoursInitial;
